import type { CastBounds } from "../FitCast/locations";
import {
  castLocationStackBounds,
  mergeCastBounds,
  mergeCastPartRow,
} from "../FitCast/metrics";
import { buildLocationRuns } from "../../location/LocationRun";
import { MARKER_DIAMETER, markerDiscAxisOffsetY } from "../../marker/MarkerMetrics/metrics";
import type { BoardSample, ChapterBoard } from "../ChapterBoard/types";
import { boardViewportXDelta } from "../BoardSample/viewportDelta";

const NAME_BELOW_DISC_PX = 88;

export function castWorldBoundsFromBoard(
  sample: BoardSample,
  board: ChapterBoard,
  viewportWidth: number,
  playheadMs: number,
): CastBounds | null {
  const runs = buildLocationRuns(board.beats);
  const activeRunIds = new Set(
    runs
      .filter((run) => playheadMs >= run.startMs && playheadMs < run.endMs)
      .map((run) => run.runId),
  );
  const deltaX = boardViewportXDelta(
    viewportWidth,
    board.referenceViewportWidth,
  );
  let bounds: CastBounds | null = null;
  const half = MARKER_DIAMETER / 2;
  for (const entry of sample.characters) {
    if (!entry.pose.visible || entry.parked === true) {
      continue;
    }
    const cx = entry.pose.x + deltaX;
    const cy = entry.pose.y + half + markerDiscAxisOffsetY();
    bounds = mergeCastBounds(bounds, {
      minX: cx - half,
      minY: cy - half,
      maxX: cx + half,
      maxY: cy + half + NAME_BELOW_DISC_PX,
    });
  }
  for (const group of sample.locationGroups) {
    if (!activeRunIds.has(group.branchId)) {
      continue;
    }
    const site = group.sitePose;
    if (!site.visible) {
      continue;
    }
    const anchorWorldX = group.parts[0]?.pose.x ?? site.x;
    const anchorX = anchorWorldX + deltaX;
    const siteX = site.x + deltaX;
    const visibleParts = group.parts.filter((part) => part.pose.visible);
    const stack = castLocationStackBounds(
      siteX,
      site.y,
      site.stackAbove === true,
      visibleParts.length,
    );
    bounds = mergeCastBounds(bounds, stack);
    for (const part of visibleParts) {
      bounds = mergeCastPartRow(
        bounds,
        anchorX,
        part.pose.partDx ?? 0,
        stack.minY,
        stack.maxY,
      );
    }
  }
  return bounds;
}
